// src/data/mock/contact.ts

/**
 * Contact Information Data Structure
 * 
 * Contact details, locations, departments, and FAQ.
 * 
 * @note Update with actual contact information
 * @note Configure email service for form submissions
 */

export interface ContactMethod {
  id: string;
  icon: string;
  title: {
    en: string;
    ar: string;
  };
  value: string;
  href: string;
  description?: {
    en: string;
    ar: string;
  };
  available?: boolean;
  responseTime?: {
    en: string;
    ar: string;
  };
  primary?: boolean;
}

export interface Branch {
  id: string;
  name: {
    en: string;
    ar: string;
  };
  address: {
    en: string;
    ar: string;
  };
  phone: string;
  email: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  image?: string;
  sports: string[];
  manager?: {
    en: string;
    ar: string;
  };
  hours: {
    weekdays: string;
    friday: string;
    saturday?: string;
  };
  main?: boolean;
}

export interface Department {
  id: string;
  name: {
    en: string;
    ar: string;
  };
  icon: string;
  email: string;
  phone?: string;
  description: {
    en: string;
    ar: string;
  };
  contactPerson?: {
    en: string;
    ar: string;
  };
}

export interface FAQ {
  id: string;
  category: 'registration' | 'programs' | 'fees' | 'facilities' | 'general';
  question: {
    en: string;
    ar: string;
  };
  answer: {
    en: string;
    ar: string;
  };
  priority?: number;
}

/**
 * Primary Contact Methods
 */
export const contactMethods: ContactMethod[] = [
  {
    id: "phone-main",
    icon: "Phone",
    primary: true,
    title: {
      en: "Main Phone",
      ar: "الهاتف الرئيسي"
    },
    value: "+970 59 123 4567",
    href: "#",
    description: {
      en: "Call us during business hours",
      ar: "اتصل بنا خلال ساعات العمل"
    },
    available: true,
    responseTime: {
      en: "Immediate",
      ar: "فوري"
    }
  },
  {
    id: "email-main",
    icon: "Mail",
    primary: true,
    title: {
      en: "Email",
      ar: "البريد الإلكتروني"
    },
    value: "info@championsacademy.ps",
    href: "mailto:info@championsacademy.ps",
    description: {
      en: "Send us a detailed message",
      ar: "أرسل لنا رسالة مفصلة"
    },
    available: true,
    responseTime: {
      en: "Within 24 hours",
      ar: "خلال 24 ساعة"
    }
  },
  {
    id: "whatsapp",
    icon: "MessageCircle",
    primary: true,
    title: {
      en: "WhatsApp",
      ar: "واتساب"
    },
    value: "+970 59 123 4567",
    href: "#",
    description: {
      en: "Quick messaging and support",
      ar: "رسائل سريعة ودعم"
    },
    available: true,
    responseTime: {
      en: "Within 1 hour",
      ar: "خلال ساعة واحدة"
    }
  },
  {
    id: "location",
    icon: "MapPin",
    title: {
      en: "Visit Us",
      ar: "زُرنا"
    },
    value: "Gaza City, Palestine",
    href: "#branches",
    description: {
      en: "7 branches across Gaza",
      ar: "7 فروع في جميع أنحاء غزة"
    },
    available: true
  }
];

/**
 * Emergency Contact
 */
export const emergencyContact = {
  phone: "+970 59 999 9999",
  available24_7: true,
  description: {
    en: "24/7 Emergency Hotline for urgent matters",
    ar: "خط ساخن طوارئ 24/7 للحالات العاجلة"
  }
};

/**
 * Branches/Locations
 */
export const branches: Branch[] = [
  {
    id: "branch-001",
    main: true,
    name: {
      en: "Main Campus - Gaza City",
      ar: "الحرم الرئيسي - مدينة غزة"
    },
    address: {
      en: "Al-Rimal District, Omar Al-Mukhtar Street, Gaza City",
      ar: "حي الرمال، شارع عمر المختار، مدينة غزة"
    },
    phone: "+970 59 123 4567",
    email: "main@championsacademy.ps",
    coordinates: {
      lat: 31.5017,
      lng: 34.4668
    },
    image: "/assets/images/s-blob-v1-IMAGE-N2M3FUp0KnU.jpeg",
    sports: ["Football", "Basketball", "Swimming", "Karate", "Tennis"],
    manager: {
      en: "Ahmed Al-Masri",
      ar: "أحمد المصري"
    },
    hours: {
      weekdays: "8:00 AM - 10:00 PM",
      friday: "2:00 PM - 10:00 PM"
    }
  },
  {
    id: "branch-002",
    name: {
      en: "Jabalia Branch",
      ar: "فرع جباليا"
    },
    address: {
      en: "Jabalia Camp, Main Street",
      ar: "مخيم جباليا، الشارع الرئيسي"
    },
    phone: "+970 59 123 4568",
    email: "jabalia@championsacademy.ps",
    image: "/assets/images/s-blob-v1-IMAGE-Myngy4tjQqo.jpeg",
    sports: ["Football", "Karate", "Table Tennis"],
    hours: {
      weekdays: "8:00 AM - 9:00 PM",
      friday: "3:00 PM - 9:00 PM"
    }
  },
  {
    id: "branch-003",
    name: {
      en: "Khan Younis Branch",
      ar: "فرع خان يونس"
    },
    address: {
      en: "Khan Younis, Palestine Street",
      ar: "خان يونس، شارع فلسطين"
    },
    phone: "+970 59 123 4569",
    email: "khanyounis@championsacademy.ps",
    image: "/assets/images/s-blob-v1-IMAGE-3-Rt3ARYBgg.jpeg",
    sports: ["Football", "Basketball", "Swimming"],
    hours: {
      weekdays: "8:00 AM - 9:00 PM",
      friday: "3:00 PM - 9:00 PM"
    }
  },
  {
    id: "branch-004",
    name: {
      en: "Rafah Branch",
      ar: "فرع رفح"
    },
    address: {
      en: "Rafah City, Salah Al-Din Street",
      ar: "مدينة رفح، شارع صلاح الدين"
    },
    phone: "+970 59 123 4570",
    email: "rafah@championsacademy.ps",
    sports: ["Football", "Karate"],
    hours: {
      weekdays: "8:00 AM - 9:00 PM",
      friday: "3:00 PM - 9:00 PM"
    }
  },
  {
    id: "branch-005",
    name: {
      en: "Deir al-Balah Branch",
      ar: "فرع دير البلح"
    },
    address: {
      en: "Deir al-Balah, Central Street",
      ar: "دير البلح، الشارع المركزي"
    },
    phone: "+970 59 123 4571",
    email: "deirbalah@championsacademy.ps",
    sports: ["Football", "Basketball"],
    hours: {
      weekdays: "8:00 AM - 9:00 PM",
      friday: "3:00 PM - 9:00 PM"
    }
  },
  {
    id: "branch-006",
    name: {
      en: "Beach Camp Branch",
      ar: "فرع الشاطئ"
    },
    address: {
      en: "Beach Camp, Gaza City",
      ar: "مخيم الشاطئ، مدينة غزة"
    },
    phone: "+970 59 123 4572",
    email: "beach@championsacademy.ps",
    sports: ["Football", "Table Tennis"],
    hours: {
      weekdays: "8:00 AM - 9:00 PM",
      friday: "3:00 PM - 9:00 PM"
    }
  },
  {
    id: "branch-007",
    name: {
      en: "Beit Hanoun Branch",
      ar: "فرع بيت حانون"
    },
    address: {
      en: "Beit Hanoun, Main Road",
      ar: "بيت حانون، الطريق الرئيسي"
    },
    phone: "+970 59 123 4573",
    email: "beithanoun@championsacademy.ps",
    sports: ["Football", "Karate"],
    hours: {
      weekdays: "8:00 AM - 9:00 PM",
      friday: "3:00 PM - 9:00 PM"
    }
  }
];

/**
 * Departments
 */
export const departments: Department[] = [
  {
    id: "dept-registration",
    name: {
      en: "Registration & Admissions",
      ar: "التسجيل والقبول"
    },
    icon: "UserPlus",
    email: "admissions@championsacademy.ps",
    phone: "+970 59 123 4567",
    description: {
      en: "New registrations, program information, and enrollment",
      ar: "التسجيلات الجديدة ومعلومات البرامج والتحاق"
    },
    contactPerson: {
      en: "Sarah Al-Hassan",
      ar: "سارة الحسن"
    }
  },
  {
    id: "dept-coaching",
    name: {
      en: "Coaching & Training",
      ar: "التدريب"
    },
    icon: "Users",
    email: "coaching@championsacademy.ps",
    phone: "+970 59 123 4568",
    description: {
      en: "Training programs, schedules, and coaching inquiries",
      ar: "برامج التدريب والجداول واستفسارات التدريب"
    },
    contactPerson: {
      en: "Coach Tariq Hamdan",
      ar: "المدرب طارق حمدان"
    }
  },
  {
    id: "dept-media",
    name: {
      en: "Media & Press",
      ar: "الإعلام والصحافة"
    },
    icon: "Camera",
    email: "press@championsacademy.ps",
    description: {
      en: "Media inquiries, press releases, and partnerships",
      ar: "الاستفسارات الإعلامية والبيانات الصحفية والشراكات"
    },
    contactPerson: {
      en: "Media Team",
      ar: "فريق الإعلام"
    }
  },
  {
    id: "dept-partnerships",
    name: {
      en: "Partnerships & Sponsorships",
      ar: "الشراكات والرعاية"
    },
    icon: "Handshake",
    email: "partnerships@championsacademy.ps",
    description: {
      en: "Corporate partnerships, sponsorships, and collaborations",
      ar: "الشراكات المؤسسية والرعاية والتعاون"
    },
    contactPerson: {
      en: "Business Development Team",
      ar: "فريق تطوير الأعمال"
    }
  },
  {
    id: "dept-support",
    name: {
      en: "General Support",
      ar: "الدعم العام"
    },
    icon: "HeadphonesIcon",
    email: "support@championsacademy.ps",
    phone: "+970 59 123 4569",
    description: {
      en: "General inquiries, feedback, and support",
      ar: "الاستفسارات العامة والملاحظات والدعم"
    }
  }
];

/**
 * FAQs by Category
 */
export const faqs: FAQ[] = [
  // Registration FAQs
  {
    id: "faq-reg-001",
    category: "registration",
    priority: 1,
    question: {
      en: "How do I register my child?",
      ar: "كيف أسجل طفلي؟"
    },
    answer: {
      en: "You can register online through our website, visit any of our 7 branches, or contact us via phone/WhatsApp. Our registration team will guide you through the process and help you choose the right program for your child.",
      ar: "يمكنك التسجيل عبر الإنترنت من خلال موقعنا، أو زيارة أي من فروعنا السبعة، أو الاتصال بنا عبر الهاتف/واتساب. سيرشدك فريق التسجيل خلال العملية ويساعدك في اختيار البرنامج المناسب لطفلك."
    }
  },
  {
    id: "faq-reg-002",
    category: "registration",
    priority: 2,
    question: {
      en: "What age groups do you accept?",
      ar: "ما هي الفئات العمرية التي تقبلونها؟"
    },
    answer: {
      en: "We accept athletes from ages 4 to 18+ across all our sports programs. Each sport has specific age divisions: Little Champions (4-6), Junior (7-10), Youth (11-14), Teen (15-18), and Adults (18+).",
      ar: "نقبل الرياضيين من سن 4 إلى 18+ في جميع برامجنا الرياضية. كل رياضة لديها أقسام عمرية محددة: الأبطال الصغار (4-6)، الناشئين (7-10)، الشباب (11-14)، المراهقين (15-18)، والبالغين (18+)."
    }
  },
  {
    id: "faq-reg-003",
    category: "registration",
    priority: 3,
    question: {
      en: "Do you offer trial sessions?",
      ar: "هل تقدمون جلسات تجريبية؟"
    },
    answer: {
      en: "Yes! We offer FREE trial sessions for all new athletes. This allows your child to experience our training environment and meet our coaches before committing. Contact us to schedule a trial session.",
      ar: "نعم! نقدم جلسات تجريبية مجانية لجميع الرياضيين الجدد. يتيح هذا لطفلك تجربة بيئة التدريب الخاصة بنا ومقابلة مدربينا قبل الالتزام. اتصل بنا لجدولة جلسة تجريبية."
    }
  },
  {
    id: "faq-reg-004",
    category: "registration",
    question: {
      en: "What documents are required for registration?",
      ar: "ما هي المستندات المطلوبة للتسجيل؟"
    },
    answer: {
      en: "You'll need: 1) Copy of birth certificate or ID, 2) Recent passport-sized photo, 3) Medical clearance form (provided by us), 4) Parent/guardian ID and contact information. Our team will guide you through the complete documentation process.",
      ar: "ستحتاج إلى: 1) نسخة من شهادة الميلاد أو الهوية، 2) صورة حديثة بحجم جواز السفر، 3) نموذج تصريح طبي (نوفره نحن)، 4) هوية الوالد/الوصي ومعلومات الاتصال. سيرشدك فريقنا خلال عملية التوثيق الكاملة."
    }
  },

  // Programs FAQs
  {
    id: "faq-prog-001",
    category: "programs",
    priority: 1,
    question: {
      en: "What sports programs do you offer?",
      ar: "ما هي البرامج الرياضية التي تقدمونها؟"
    },
    answer: {
      en: "We offer comprehensive programs in: Football (Soccer), Basketball, Swimming, Karate, Table Tennis, Tennis, Volleyball, and Gymnastics. Each program includes skill development, competitive training, and character building.",
      ar: "نقدم برامج شاملة في: كرة القدم، كرة السلة، السباحة، الكاراتيه، تنس الطاولة، التنس، الكرة الطائرة، والجمباز. يتضمن كل برنامج تطوير المهارات والتدريب التنافسي وبناء الشخصية."
    }
  },
  {
    id: "faq-prog-002",
    category: "programs",
    question: {
      en: "Can my child join multiple sports?",
      ar: "هل يمكن لطفلي الانضمام إلى رياضات متعددة؟"
    },
    answer: {
      en: "Absolutely! We encourage multi-sport participation as it develops well-rounded athletes. We offer special multi-sport packages with discounted rates. Our schedulers will help coordinate training times to avoid conflicts.",
      ar: "بالتأكيد! نشجع المشاركة في رياضات متعددة لأنها تطور رياضيين متكاملين. نقدم باقات رياضية متعددة خاصة بأسعار مخفضة. سيساعد منسقو الجداول لدينا في تنسيق أوقات التدريب لتجنب التعارضات."
    }
  },
  {
    id: "faq-prog-003",
    category: "programs",
    question: {
      en: "How often are training sessions?",
      ar: "ما مدى تكرار جلسات التدريب؟"
    },
    answer: {
      en: "Training frequency varies by program level: Recreational (2x/week), Competitive (3-4x/week), Elite (5-6x/week). Each session is 60-90 minutes depending on age group and sport.",
      ar: "يختلف تكرار التدريب حسب مستوى البرنامج: ترفيهي (مرتان/أسبوعياً)، تنافسي (3-4 مرات/أسبوعياً)، نخبة (5-6 مرات/أسبوعياً). كل جلسة من 60-90 دقيقة حسب الفئة العمرية والرياضة."
    }
  },

  // Fees FAQs
  {
    id: "faq-fees-001",
    category: "fees",
    priority: 1,
    question: {
      en: "What are the program fees?",
      ar: "ما هي رسوم البرنامج؟"
    },
    answer: {
      en: "Fees vary by sport, level, and frequency. We offer monthly, quarterly, and annual payment plans. Contact our admissions team for detailed pricing. We also offer family discounts for siblings and multi-sport packages.",
      ar: "تختلف الرسوم حسب الرياضة والمستوى والتكرار. نقدم خطط دفع شهرية وفصلية وسنوية. اتصل بفريق القبول للحصول على تسعير مفصل. نقدم أيضاً خصومات عائلية للأشقاء وباقات رياضية متعددة."
    }
  },
  {
    id: "faq-fees-002",
    category: "fees",
    priority: 2,
    question: {
      en: "Are scholarships available?",
      ar: "هل تتوفر منح دراسية؟"
    },
    answer: {
      en: "Yes! We offer need-based and merit-based scholarships for talented athletes and families facing financial hardship. Scholarships can cover 25-100% of fees. Applications are reviewed quarterly. Contact our admissions team for the application process.",
      ar: "نعم! نقدم منحاً دراسية قائمة على الحاجة والجدارة للرياضيين الموهوبين والعائلات التي تواجه صعوبات مالية. يمكن أن تغطي المنح 25-100٪ من الرسوم. تُراجع الطلبات كل ثلاثة أشهر. اتصل بفريق القبول لعملية التقديم."
    }
  },
  {
    id: "faq-fees-003",
    category: "fees",
    question: {
      en: "What is included in the fees?",
      ar: "ما الذي تشمله الرسوم؟"
    },
    answer: {
      en: "Fees include: Professional coaching, facility access, basic training equipment, academy uniform, insurance coverage, progress reports, and participation in internal competitions. Additional costs may apply for external tournaments and specialized equipment.",
      ar: "تشمل الرسوم: التدريب المهني، الوصول إلى المرافق، معدات التدريب الأساسية، زي الأكاديمية، التغطية التأمينية، تقارير التقدم، والمشاركة في المنافسات الداخلية. قد تطبق تكاليف إضافية للبطولات الخارجية والمعدات المتخصصة."
    }
  },

  // Facilities FAQs
  {
    id: "faq-fac-001",
    category: "facilities",
    question: {
      en: "What facilities do you have?",
      ar: "ما هي المرافق المتوفرة لديكم؟"
    },
    answer: {
      en: "Our main campus features: Olympic-standard swimming pool, multiple football pitches (outdoor/indoor), basketball courts, tennis courts, karate dojo, fitness center, changing rooms with showers, cafeteria, medical clinic, and parent waiting areas. Facilities vary by branch.",
      ar: "يحتوي حرمنا الرئيسي على: مسبح بمواصفات أولمبية، ملاعب كرة قدم متعددة (خارجية/داخلية)، ملاعب كرة سلة، ملاعب تنس، دوجو كاراتيه، مركز لياقة، غرف تغيير مع دش، كافتيريا، عيادة طبية، ومناطق انتظار الأهل. تختلف المرافق حسب الفرع."
    }
  },
  {
    id: "faq-fac-002",
    category: "facilities",
    question: {
      en: "Are parents allowed to watch training?",
      ar: "هل يُسمح للأهل بمشاهدة التدريب؟"
    },
    answer: {
      en: "Yes! We have designated viewing areas where parents can watch training sessions. We encourage parental involvement and hold regular parent-coach meetings to discuss athlete progress.",
      ar: "نعم! لدينا مناطق مشاهدة مخصصة حيث يمكن للأهل مشاهدة جلسات التدريب. نشجع مشاركة الأهل ونعقد اجتماعات منتظمة بين الأهل والمدربين لمناقشة تقدم الرياضي."
    }
  },

  // General FAQs
  {
    id: "faq-gen-001",
    category: "general",
    priority: 1,
    question: {
      en: "What are your operating hours?",
      ar: "ما هي ساعات العمل؟"
    },
    answer: {
      en: "Main campus hours: Saturday-Thursday 8:00 AM - 10:00 PM, Friday 2:00 PM - 10:00 PM. Branch hours may vary. Training sessions are scheduled throughout the day in age-appropriate time slots.",
      ar: "ساعات الحرم الرئيسي: السبت-الخميس 8:00 صباحاً - 10:00 مساءً، الجمعة 2:00 ظهراً - 10:00 مساءً. قد تختلف ساعات الفروع. يتم جدولة جلسات التدريب طوال اليوم في أوقات مناسبة للفئات العمرية."
    }
  },
  {
    id: "faq-gen-002",
    category: "general",
    question: {
      en: "Do you provide transportation?",
      ar: "هل توفرون وسائل نقل؟"
    },
    answer: {
      en: "We offer optional bus transportation service covering major areas in Gaza. Transportation fees are separate from program fees. Routes and schedules are available from our admissions team.",
      ar: "نقدم خدمة نقل بالحافلات اختيارية تغطي المناطق الرئيسية في غزة. رسوم النقل منفصلة عن رسوم البرنامج. المسارات والجداول متاحة من فريق القبول."
    }
  },
  {
    id: "faq-gen-003",
    category: "general",
    question: {
      en: "What safety measures do you have in place?",
      ar: "ما هي إجراءات السلامة المتبعة؟"
    },
    answer: {
      en: "Safety is our top priority. We have: Certified coaches with first aid training, on-site medical staff, comprehensive insurance, secure facilities with controlled access, CCTV monitoring, emergency protocols, and age-appropriate equipment. All activities follow international safety standards.",
      ar: "السلامة أولويتنا القصوى. لدينا: مدربون معتمدون مع تدريب إسعافات أولية، طاقم طبي في الموقع، تأمين شامل، مرافق آمنة بوصول محكوم، مراقبة CCTV، بروتوكولات طوارئ، ومعدات مناسبة للعمر. جميع الأنشطة تتبع معايير السلامة الدولية."
    }
  }
];

/**
 * Contact Form Subjects
 */
export const contactSubjects = [
  { value: "registration", label: { en: "Registration & Enrollment", ar: "التسجيل والالتحاق" } },
  { value: "programs", label: { en: "Program Information", ar: "معلومات البرامج" } },
  { value: "fees", label: { en: "Fees & Payment", ar: "الرسوم والدفع" } },
  { value: "trial", label: { en: "Trial Session Request", ar: "طلب جلسة تجريبية" } },
  { value: "scholarship", label: { en: "Scholarship Inquiry", ar: "استفسار منحة" } },
  { value: "media", label: { en: "Media & Press", ar: "الإعلام والصحافة" } },
  { value: "partnership", label: { en: "Partnership/Sponsorship", ar: "شراكة/رعاية" } },
  { value: "feedback", label: { en: "Feedback & Suggestions", ar: "ملاحظات واقتراحات" } },
  { value: "complaint", label: { en: "Complaint", ar: "شكوى" } },
  { value: "other", label: { en: "Other", ar: "أخرى" } }
];

/**
 * Social Media Links (if not imported from config)
 */
export const socialMediaLinks = [
  { platform: "Facebook", url: "https://facebook.com/championsacademy", icon: "Facebook" },
  { platform: "Instagram", url: "https://instagram.com/championsacademy", icon: "Instagram" },
  { platform: "Twitter/X", url: "https://twitter.com/championsacademy", icon: "Twitter" },
  { platform: "YouTube", url: "https://youtube.com/@championsacademy", icon: "Youtube" },
  { platform: "TikTok", url: "https://tiktok.com/@championsacademy", icon: "Music" },
  { platform: "LinkedIn", url: "https://linkedin.com/company/championsacademy", icon: "Linkedin" }
];

/**
 * Quick Stats
 */
export const contactStats = {
  branches: branches.length,
  responseTime: "< 24 hours",
  availability: "24/7 Emergency",
  languages: ["Arabic", "English"]
};
